import {
  dayjsWithTimezone,
  disabledDateRule,
  validateMaxThreeDigit,
  validateMaxTwoDigit,
  validateNric,
  validatePhoneNumber,
  validatePostalCode,
  validDateRule,
} from '@healthhub/react-ui';
import type { FormInstance } from 'antd';
import type { Rule, RuleObject, RuleRender } from 'antd/es/form';

import { QuestionType } from '@shared/services/appointment/form/types/appointmentForm.type';
import type { QuestionnaireValidation } from '@shared/services/appointment/questionnaire/types/getQuestionnaire.type';
import { ValidationsKeys } from '@shared/services/appointment/questionnaire/types/getQuestionnaire.type';
import type { Validation } from '@shared/types/form.types';
import { getValidationMessage } from '@shared/utils/form';
import { emailRule } from '@shared/utils/validator';

import { getFormRequiredMessage } from './form';
import {
  MAMMOGRAM_QUESTION_CONTACT_NUMBER_IDS,
  MAMMOGRAM_QUESTION_COVID_DATE_ID,
  MAMMOGRAM_QUESTION_EMAIL_ADDRESS_IDS,
  MAMMOGRAM_QUESTION_NAME_ID,
  MAMMOGRAM_QUESTION_YEAR_BREAST_CANCER,
  MAMMOGRAM_QUESTION_YEAR_DISCHARGED,
} from '../AppointmentMakeNew/constants/makeNew';
import { CervicalQuestionIds } from '../AppointmentMakeNew/types/makeNew';
import {
  displayValidateMessage,
  handPhoneNumberRule,
  nameRule,
  onlyNumberRule,
  requiredRule,
  validationMessageRule,
} from '../shared/utils/form-rules';

export const onlyTextAndSpaceAndUnderscoreDashRule = (message = ''): Rule => ({
  validator(_rule, value) {
    if (!value) {
      return Promise.resolve();
    }
    const ONLY_TEXT_AND_SPACE_UNDERSCORE_REGEX = /^[A-Za-z_ -]+$/;
    const regEx = new RegExp(ONLY_TEXT_AND_SPACE_UNDERSCORE_REGEX);
    return regEx.test(value) ? Promise.resolve() : Promise.reject(new Error(message));
  },
  message: displayValidateMessage({ message }),
});

export const underscoreDashShouldNotStartEndTextRule = (message = ''): Rule => ({
  validator(_rule, value) {
    if (!value) {
      return Promise.resolve();
    }
    const ONLY_TEXT_AND_SPACE_UNDERSCORE_REGEX = '^(?![_-])[A-Za-z _-]*(?<![_-])$';

    const regEx = new RegExp(ONLY_TEXT_AND_SPACE_UNDERSCORE_REGEX);
    return regEx.test(value) ? Promise.resolve() : Promise.reject(new Error(message));
  },
  message: displayValidateMessage({ message }),
});

export const phoneNumberRule =
  (message = ''): Rule =>
  (): RuleObject => ({
    validator(_rule, value) {
      if (!validatePhoneNumber(String(value)) && !!value) {
        return Promise.reject(new Error(message));
      }
      return Promise.resolve();
    },
    message: displayValidateMessage({ message }),
  });

export const nricRule = (message = ''): Rule => ({
  validator(_, value) {
    if (!validateNric(value) && !!value) {
      return Promise.reject(new Error(message));
    }

    return Promise.resolve();
  },
  message: displayValidateMessage({ message }),
});

const ageRule = (message = ''): Rule => ({
  validator(_, value) {
    if (!validateMaxThreeDigit(value) && !!value) {
      return Promise.reject(new Error(message));
    }
    return Promise.resolve();
  },
  message: displayValidateMessage({ message }),
});

const heightAndWeightRule = (message = ''): Rule => ({
  validator(_, value) {
    if (!validateMaxThreeDigit(value) && !!value) {
      return Promise.reject(new Error(message));
    }
    if (String(value) === '0') {
      return Promise.reject(new Error('Must be greater than zero'));
    }
    return Promise.resolve();
  },
  message: displayValidateMessage({ message }),
});

const maxTwoDigitalRule = (message = ''): Rule => ({
  validator(_, value) {
    if (!validateMaxTwoDigit(String(value)) && !!value) {
      return Promise.reject(new Error(message));
    }

    return Promise.resolve();
  },
  message: displayValidateMessage({ message }),
});

const postalCodeRule = (message = ''): Rule => ({
  validator(_, value) {
    if (!validatePostalCode(value) && !!value) {
      return Promise.reject(new Error(message));
    }

    return Promise.resolve();
  },
  message: displayValidateMessage({ message }),
});

const validationMatchRule =
  ({
    _onClickValidateMessage: _ignore,
    formValues,
    validations,
  }: {
    _onClickValidateMessage?: () => void;
    formValues: FormInstance;
    validations?: Validation[];
  }): RuleRender =>
  (): RuleObject => ({
    validator(_, value) {
      if (validations) {
        const matchValidation = validations?.find((validation) => validation.Type === 'match' && validation.Field);
        let valueMatches = matchValidation?.Field ? value !== formValues[matchValidation.Field] : false;

        if (validations.some((validation) => MAMMOGRAM_QUESTION_EMAIL_ADDRESS_IDS.includes(validation.Field ?? ''))) {
          // email match validation should allow different case
          valueMatches = matchValidation?.Field
            ? value.toLowerCase() !== formValues[matchValidation.Field].toLowerCase()
            : false;
        }
        if (matchValidation && valueMatches) {
          return Promise.reject(new Error(matchValidation.Message));
        }
      }

      return Promise.resolve();
    },
    message: validations?.[0]?.Message
      ? displayValidateMessage({
          message: validations?.[0]?.Message,
        })
      : undefined,
  });

const validationGroupRule =
  ({
    _onClickValidateMessage: _ignore,
    dependValue,
    validations,
  }: {
    _onClickValidateMessage?: () => void;
    dependValue?: string;
    validations?: Validation[];
  }): RuleRender =>
  (): RuleObject => ({
    validator(_, value) {
      const validationMessage = getValidationMessage({
        validations,
        value,
      });

      if (validationMessage && value === 'No') {
        return Promise.reject(new Error(validationMessage));
      }
      if (validationMessage && value === 'Yes' && dependValue === 'Yes') {
        return Promise.reject(new Error(validationMessage));
      }
      if (validationMessage && value === 'Not Applicable' && dependValue === 'Not Applicable') {
        return Promise.reject(new Error(validationMessage));
      }
      return Promise.resolve();
    },
  });

export const getQuestionnaireRule = (validation: QuestionnaireValidation): Rule => {
  switch (validation?.Key) {
    case ValidationsKeys.AdditionalPhoneNumber:
      return phoneNumberRule('Please enter a valid mobile number');
    case ValidationsKeys.Age:
      return ageRule('Please enter a valid format');
    case ValidationsKeys.Email:
      return emailRule('Please enter a valid email.');
    case ValidationsKeys.Height:
      return heightAndWeightRule('Height in cm, no decimal points and max 3 digits.');
    case ValidationsKeys.MaxTwoDigitals:
      return maxTwoDigitalRule('Max 2 digits and allowed to input 0.');
    case ValidationsKeys.Nric:
      return nricRule('Invalid NRIC.');
    case ValidationsKeys.PhoneNumber:
      return handPhoneNumberRule('Please enter a valid mobile number');
    case ValidationsKeys.PostalCode:
      return postalCodeRule('Please enter a valid postal code format');
    case ValidationsKeys.Weight:
      return heightAndWeightRule('Weight in kilograms, no decimal points and max 3 digits.');
    default:
      return {
        validator() {
          return Promise.resolve();
        },
      };
  }
};

export const getMammogramFormRules = ({
  form,
  linkId,
  onNavigateRequestForm,
  required,
  type,
  validations,
}: {
  form: FormInstance;
  linkId: string;
  onNavigateRequestForm: () => void;
  required?: boolean;
  type: string;
  validations?: any;
}) => {
  const rules = [requiredRule({ message: getFormRequiredMessage(linkId), required })];

  if (validations) {
    rules.push(
      validationMessageRule({
        onClickValidateMessage: onNavigateRequestForm,
        validation: validations,
      })
    );
  }

  if (linkId === MAMMOGRAM_QUESTION_NAME_ID) {
    rules.push(nameRule());
  }

  if (type === QuestionType.DateInput) {
    rules.push(
      onlyNumberRule('Please enter a valid number.'),
      { max: 4, message: displayValidateMessage({ message: 'Please enter no more than 4 characters' }) },
      { message: displayValidateMessage({ message: 'Please enter at least 4 characters.' }), min: 4 }
    );

    if (linkId === MAMMOGRAM_QUESTION_YEAR_DISCHARGED) {
      rules.push({
        validator(_, yearDischarged) {
          const yearBreastCancer = form.getFieldValue(MAMMOGRAM_QUESTION_YEAR_BREAST_CANCER);

          if (!yearDischarged || !yearBreastCancer) {
            return Promise.resolve();
          }

          return yearDischarged < yearBreastCancer
            ? Promise.reject(new Error('The Year of Discharged should not be earlier than the Year of Breast Cancer.'))
            : Promise.resolve();
        },
        message: displayValidateMessage({
          message: 'The Year of Discharged should not be earlier than the Year of Breast Cancer.',
        }),
      });
    }
  }

  if (type === QuestionType.Date) {
    rules.push(
      validDateRule(),
      disabledDateRule(
        linkId === MAMMOGRAM_QUESTION_COVID_DATE_ID
          ? (current) => !!current.isBefore(dayjsWithTimezone().subtract(6, 'weeks').startOf('day'))
          : (current) => !!current?.isAfter(dayjsWithTimezone().endOf('day'))
      )
    );
  }

  if (type === QuestionType.Text && MAMMOGRAM_QUESTION_EMAIL_ADDRESS_IDS.includes(linkId)) {
    rules.push(
      emailRule('Please enter a valid email address.'),
      validationMatchRule({
        formValues: form.getFieldsValue(MAMMOGRAM_QUESTION_EMAIL_ADDRESS_IDS),
        validations,
      })
    );
  }

  if (type === QuestionType.Integer) {
    rules.push(
      handPhoneNumberRule('Please enter valid Handphone number.'),
      validationMatchRule({
        formValues: form.getFieldsValue(MAMMOGRAM_QUESTION_CONTACT_NUMBER_IDS),
        validations,
      })
    );
  }

  return rules;
};

export const getCervicalFormRules = ({
  linkId,
  onNavigateRequestForm,
  required,
  ageCheckHpvDnaField,
  ageCheckPapSmearField,
  type,
  validations,
  description,
}: {
  ageCheckHpvDnaField: string;
  ageCheckPapSmearField: string;
  description?: string;
  linkId: string;
  onNavigateRequestForm: () => void;
  required?: boolean;
  type: string;
  validations?: any;
}) => {
  const rules = [
    requiredRule({
      message: description || 'Required',
      required: required ?? false,
    }),
  ];

  if (validations) {
    rules.push(
      linkId === CervicalQuestionIds.ageCheckPapSmear || linkId === CervicalQuestionIds.ageCheckHpvDna
        ? validationGroupRule({
            dependValue: linkId === CervicalQuestionIds.ageCheckPapSmear ? ageCheckHpvDnaField : ageCheckPapSmearField,
            _onClickValidateMessage: onNavigateRequestForm,
            validations,
          })
        : validationMessageRule({
            onClickValidateMessage: onNavigateRequestForm,
            validation: validations,
          })
    );
  }

  if (linkId === CervicalQuestionIds.patientName) {
    rules.push(nameRule());
  }

  switch (type) {
    case QuestionType.DateInput:
      rules.push(
        onlyNumberRule('Please enter a valid number.'),
        { max: 4, message: displayValidateMessage({ message: 'Please enter no more than 4 characters' }) },
        { message: displayValidateMessage({ message: 'Please enter at least 4 characters.' }), min: 4 }
      );
      break;
    case QuestionType.Integer:
      rules.push(handPhoneNumberRule('Please enter valid Handphone number.'));
      break;
    case QuestionType.Text:
      if (linkId === CervicalQuestionIds.emailAddress) {
        rules.push(emailRule('Please enter a valid email address.'));
      }
      break;
    default:
      break;
  }

  return rules;
};

// Temporary export to avoid major Appt changes, will remove once all Appt forms have been migrated to use below shared form rules from utils
export { displayValidateMessage, handPhoneNumberRule, nameRule, requiredRule } from '../shared/utils/form-rules';
export { emailRule } from '@shared/utils/validator';
